/**
 * 본인인증 과정 추적을 위한 로깅 유틸리티
 * Sentry를 활용하여 인증 과정의 각 단계를 기록
 */

import * as Sentry from '@sentry/react';

// 인증 세션 정보
interface AuthSession {
  sessionId: string;
  authMethod: 'mobile' | 'easy' | null;
  startTime: number;
  browserType: string;
  userAgent: string;
  userId?: number;
  txId?: string;
}

// 로그 이벤트 타입
type AuthEventType =
  | 'AUTH_START'           // 인증 시작
  | 'SDK_LOAD'             // SDK 로드
  | 'SDK_CALL'             // SDK 함수 호출
  | 'CALLBACK_RECEIVED'    // 콜백 수신
  | 'API_REQUEST'          // API 요청
  | 'API_RESPONSE'         // API 응답
  | 'AUTH_SUCCESS'         // 인증 성공
  | 'AUTH_ERROR'           // 인증 에러
  | 'AUTH_CANCEL'          // 인증 취소
  | 'REDIRECT_SUCCESS'     // Redirect 방식 인증 성공
  | 'REDIRECT_ERROR';      // Redirect 방식 인증 에러

// 로그 레벨
type LogLevel = 'info' | 'warning' | 'error';

class AuthLogger {
  private currentSession: AuthSession | null = null;
  private logs: Array<{
    timestamp: number;
    event: AuthEventType;
    level: LogLevel;
    data: Record<string, any>;
  }> = [];

  /**
   * 고유 세션 ID 생성
   */
  private generateSessionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    return `AUTH_${timestamp}_${random}`;
  }

  /**
   * 브라우저/디바이스 정보 수집
   */
  private collectDeviceInfo(): Record<string, any> {
    const ua = navigator.userAgent;

    // Flutter 앱 WebView 체크
    const isFlutterApp = localStorage.getItem('mobileApp') !== null &&
                         typeof (window as any).flutter_inappwebview !== 'undefined';

    // 인앱 브라우저 감지
    const inAppBrowser = this.detectInAppBrowser(ua);

    // iOS/Android 감지
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);

    // Safari 감지 (iOS에서 중요)
    const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|FxiOS/i.test(ua);

    return {
      userAgent: ua,
      isFlutterApp,
      inAppBrowser,
      isIOS,
      isAndroid,
      isSafari,
      isMobile: isIOS || isAndroid,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      // Storage 테스트
      localStorageAvailable: this.testStorage('localStorage'),
      sessionStorageAvailable: this.testStorage('sessionStorage'),
    };
  }

  /**
   * 인앱 브라우저 감지
   */
  private detectInAppBrowser(ua: string): string | null {
    const lowerUA = ua.toLowerCase();

    if (lowerUA.includes('kakaotalk')) return 'KakaoTalk';
    if (lowerUA.includes('naver')) return 'Naver';
    if (lowerUA.includes('instagram')) return 'Instagram';
    if (lowerUA.includes('fban') || lowerUA.includes('fbav')) return 'Facebook';
    if (lowerUA.includes('line')) return 'Line';
    if (lowerUA.includes('twitter')) return 'Twitter';
    if (lowerUA.includes('band')) return 'Band';

    // iOS에서 Safari가 아닌 경우 (일반적인 인앱 브라우저)
    if (/iPhone|iPad/i.test(ua) && !/Safari/i.test(ua)) return 'Unknown iOS InApp';

    return null;
  }

  /**
   * Storage 사용 가능 여부 테스트
   */
  private testStorage(type: 'localStorage' | 'sessionStorage'): boolean {
    try {
      const storage = window[type];
      const testKey = '__auth_logger_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 인증 세션 시작
   */
  startSession(authMethod: 'mobile' | 'easy', userId?: number): string {
    const sessionId = this.generateSessionId();
    const deviceInfo = this.collectDeviceInfo();

    this.currentSession = {
      sessionId,
      authMethod,
      startTime: Date.now(),
      browserType: this.detectBrowserType(),
      userAgent: navigator.userAgent,
      userId,
    };

    this.logs = [];

    // Sentry에 세션 컨텍스트 설정
    Sentry.setContext('auth_session', {
      sessionId,
      authMethod,
      ...deviceInfo,
    });

    // Sentry에 태그 설정 (검색/필터링용)
    Sentry.setTag('auth_session_id', sessionId);
    Sentry.setTag('auth_method', authMethod);
    Sentry.setTag('browser_type', this.currentSession.browserType);

    if (deviceInfo.inAppBrowser) {
      Sentry.setTag('in_app_browser', deviceInfo.inAppBrowser);
    }

    this.log('AUTH_START', 'info', {
      sessionId,
      authMethod,
      userId,
      deviceInfo,
    });

    console.log(`[AuthLogger] Session started: ${sessionId}`, deviceInfo);

    return sessionId;
  }

  /**
   * 브라우저 타입 감지 (EZ-IOK용)
   */
  private detectBrowserType(): 'WB' | 'MB' | 'MWV' {
    const isFlutterApp = localStorage.getItem('mobileApp') !== null &&
                         typeof (window as any).flutter_inappwebview !== 'undefined';

    if (isFlutterApp) {
      return 'MWV';
    }

    const userAgent = navigator.userAgent.toLowerCase();
    const isWebView =
      userAgent.includes('mobileapp') ||
      userAgent.includes('wv') ||
      ((userAgent.includes('iphone') || userAgent.includes('ipad')) &&
        !userAgent.includes('safari'));

    if (isWebView) {
      return 'MWV';
    }

    const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
    return isMobile ? 'MB' : 'WB';
  }

  /**
   * txId 설정
   */
  setTxId(txId: string): void {
    if (this.currentSession) {
      this.currentSession.txId = txId;
      Sentry.setTag('auth_tx_id', txId);
    }
  }

  /**
   * 로그 기록
   */
  log(event: AuthEventType, level: LogLevel, data: Record<string, any> = {}): void {
    const logEntry = {
      timestamp: Date.now(),
      event,
      level,
      data: {
        ...data,
        sessionId: this.currentSession?.sessionId,
        txId: this.currentSession?.txId,
      },
    };

    this.logs.push(logEntry);

    // 콘솔 출력
    const prefix = `[AuthLogger:${event}]`;
    if (level === 'error') {
      console.error(prefix, data);
    } else if (level === 'warning') {
      console.warn(prefix, data);
    } else {
      console.log(prefix, data);
    }

    // Sentry Breadcrumb 추가 (에러 발생 시 이전 이벤트들이 함께 전송됨)
    Sentry.addBreadcrumb({
      category: 'auth',
      message: event,
      level: level === 'error' ? 'error' : level === 'warning' ? 'warning' : 'info',
      data: logEntry.data,
    });
  }

  /**
   * SDK 로드 이벤트
   */
  logSDKLoad(sdkType: 'MobileOK' | 'EZIOK', success: boolean, error?: Error): void {
    this.log('SDK_LOAD', success ? 'info' : 'error', {
      sdkType,
      success,
      error: error?.message,
    });
  }

  /**
   * SDK 호출 이벤트
   */
  logSDKCall(sdkType: 'MobileOK' | 'EZIOK', params: Record<string, any>): void {
    this.log('SDK_CALL', 'info', {
      sdkType,
      ...params,
    });
  }

  /**
   * 콜백 수신 이벤트
   */
  logCallbackReceived(callbackType: 'mobileOK' | 'eziok', rawResponse: string, parsed?: Record<string, any>): void {
    this.log('CALLBACK_RECEIVED', 'info', {
      callbackType,
      rawResponse: rawResponse.substring(0, 500), // 길이 제한
      parsed,
    });
  }

  /**
   * API 요청 이벤트
   */
  logAPIRequest(endpoint: string, params: Record<string, any>): void {
    this.log('API_REQUEST', 'info', {
      endpoint,
      params: this.sanitizeParams(params),
    });
  }

  /**
   * API 응답 이벤트
   */
  logAPIResponse(endpoint: string, response: Record<string, any>, success: boolean): void {
    this.log('API_RESPONSE', success ? 'info' : 'error', {
      endpoint,
      response: this.sanitizeParams(response),
      success,
    });
  }

  /**
   * 민감 정보 제거
   */
  private sanitizeParams(params: Record<string, any>): Record<string, any> {
    const sensitiveKeys = ['ci', 'di', 'mobileNo', 'birthDate', 'token', 'mokToken', 'keyToken', 'access_token'];
    const sanitized = { ...params };

    for (const key of sensitiveKeys) {
      if (sanitized[key]) {
        sanitized[key] = '[REDACTED]';
      }
    }

    return sanitized;
  }

  /**
   * 인증 성공
   */
  logSuccess(result: Record<string, any>): void {
    const duration = this.currentSession
      ? Date.now() - this.currentSession.startTime
      : 0;

    this.log('AUTH_SUCCESS', 'info', {
      duration,
      resultCode: result.resultCode,
      authMethod: result.authMethod,
    });

    // 성공도 Sentry에 이벤트로 기록 (통계용)
    Sentry.captureMessage('Auth Success', {
      level: 'info',
      tags: {
        auth_session_id: this.currentSession?.sessionId,
        auth_method: this.currentSession?.authMethod,
        duration_ms: String(duration),
      },
    });
  }

  /**
   * 인증 에러 - Sentry에 에러 전송
   */
  logError(error: Error | string, context?: Record<string, any>): void {
    const duration = this.currentSession
      ? Date.now() - this.currentSession.startTime
      : 0;

    const errorObj = typeof error === 'string' ? new Error(error) : error;

    this.log('AUTH_ERROR', 'error', {
      error: errorObj.message,
      stack: errorObj.stack,
      duration,
      ...context,
    });

    // Sentry에 에러 전송 (전체 로그 포함)
    Sentry.captureException(errorObj, {
      tags: {
        auth_session_id: this.currentSession?.sessionId || 'unknown',
        auth_method: this.currentSession?.authMethod || 'unknown',
        browser_type: this.currentSession?.browserType || 'unknown',
        tx_id: this.currentSession?.txId || 'unknown',
      },
      contexts: {
        auth_session: {
          ...this.currentSession,
          duration,
        },
        auth_logs: {
          logs: this.logs.slice(-20), // 최근 20개 로그
        },
        auth_context: context,
      },
    });
  }

  /**
   * 인증 취소
   */
  logCancel(reason?: string): void {
    this.log('AUTH_CANCEL', 'warning', { reason });
  }

  /**
   * 세션 종료
   */
  endSession(): void {
    if (this.currentSession) {
      console.log(`[AuthLogger] Session ended: ${this.currentSession.sessionId}`);
    }
    this.currentSession = null;
    this.logs = [];

    // Sentry 컨텍스트 클리어
    Sentry.setContext('auth_session', null);
  }

  /**
   * 현재 세션 ID 반환
   */
  getSessionId(): string | null {
    return this.currentSession?.sessionId || null;
  }

  /**
   * 현재 세션의 모든 로그 반환 (디버깅용)
   */
  getLogs(): typeof this.logs {
    return [...this.logs];
  }
}

// 싱글톤 인스턴스
export default new AuthLogger();